import axiosInst from '@/utils/axiosinstance';

/**
 * Fire-and-forget interaction / click tracking.
 *
 * Records a UI interaction to the backend `interactionEvents` collection so the
 * admin "Interaction Analytics" page can report on button/menu usage. It NEVER
 * throws and NEVER blocks navigation — the POST is dispatched and forgotten, and
 * every error is swallowed so tracking can never break a user action.
 *
 * Usage:
 *   import { trackInteraction } from '@/utils/trackInteraction';
 *   trackInteraction('navbar', 'deals');
 *   trackInteraction('view-listing', 'contact-owner', { label: serviceId, role: 'guest' });
 *
 * @param {'view-listing'|'navbar'|'search'|'page-view'|'auth'|'pagination'|'profile'} category  High-level group (must match backend allow-list).
 * @param {string} action   The specific button/menu, e.g. 'contact-owner', 'ai-search'.
 * @param {{label?: string, role?: string, extra?: object}} [opts]
 */
export function trackInteraction(category, action, opts = {}) {
  try {
    const payload = {
      category,
      action,
      label: opts.label != null ? String(opts.label) : '',
      role: opts.role || '',
      path: typeof window !== 'undefined' && window.location ? window.location.pathname : '',
      meta: opts.extra && typeof opts.extra === 'object' ? opts.extra : undefined,
    };
    // Fire-and-forget: no await, swallow every error.
    axiosInst.post('/api/track-event', payload).catch(() => {});
  } catch (_) {
    /* never let tracking surface an error */
  }
}

/**
 * Pagination click helper — shared by every list pager so the `direction`
 * classification is derived once instead of in each caller.
 *
 * Callers pass raw page numbers; this works out whether the click was Next,
 * Previous, a jump to a numbered page, or the last-page shortcut. A no-op page
 * change (clicking the page you are already on) is dropped so the report is not
 * padded with clicks that changed nothing.
 *
 * @param {{from: number, to: number, totalPages?: number, listType?: string, view?: string}} p
 */
export function trackPageChange(p = {}) {
  const from = Number(p.from) || 0;
  const to = Number(p.to) || 0;
  if (!to || to === from) return;
  const total = Number(p.totalPages) || 0;
  let direction;
  if (to === from + 1) direction = 'next';
  else if (to === from - 1) direction = 'prev';
  else if (total && to === total) direction = 'last';
  else direction = 'jump';

  trackInteraction('pagination', 'page-change', {
    label: String(to),
    extra: {
      from,
      to,
      direction,
      totalPages: total || undefined,
      listType: p.listType || undefined,
      view: p.view || undefined,
    },
  });
}

export default trackInteraction;
